// P17a — Page /contrats : liste CRUD complète
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Trash2, Plus, FileDown } from "lucide-react";
import { useCan } from "@/hooks/useCan";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";
import { useT } from "@/lib/i18n";
interface Contrat {
  id: number;
  site_id: number;
  label: string;
  conditions: string | null;
  created_at?: string;
  updated_at?: string;
}

interface Site {
  id: number;
  name: string;
}

async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options.headers },
    ...options,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const contratsApi = {
  list: () => fetchJson<Contrat[]>(`/v1/contrats`),
  create: (data: { site_id: number; label: string; conditions?: string }) =>
    fetchJson<Contrat>(`/v1/contrats`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: number, data: Partial<Contrat>) =>
    fetchJson<Contrat>(`/v1/contrats/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  remove: (id: number) => fetchJson<void>(`/v1/contrats/${id}`, { method: "DELETE" }),
};

const sitesApi = {
  list: () => fetchJson<Site[]>(`/v1/sites`),
};

function ContratModal({
  onClose,
  sites,
  initial,
}: {
  onClose: () => void;
  sites: Site[];
  initial?: Contrat;
}) {
  const t = useT();
  const { toast } = useToast();
  const [siteId, setSiteId] = useState<number | "">(initial?.site_id ?? "");
  const [label, setLabel] = useState(initial?.label ?? "");
  const [conditions, setConditions] = useState(initial?.conditions ?? "");
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      if (!siteId) throw new Error(t("common.site_required"));
      const data = {
        site_id: siteId,
        label: label.trim(),
        conditions: conditions.trim() || undefined,
      };
      return initial ? contratsApi.update(initial.id, data) : contratsApi.create(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contrats"] });
      onClose();
      toast({
        title: initial ? "Contrat modifié" : "Contrat créé",
        variant: "success",
      });
    },
    onError: (err) => {
      toast({
        title: "Erreur",
        description: (err as Error).message,
        variant: "error",
      });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(32rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>
            {initial ? t("common.edit") : t("common.new")} {t("common.contract").toLowerCase()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <label className="text-xs">{t("common.site")} *</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={siteId}
                onChange={(e) => setSiteId(Number(e.target.value) || "")}
              >
                <option value="">{ht("— Choisir un site —")}</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs">{ht("Libellé *")}</label>
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={ht("Ex: Supervision mensuelle")}
              />
            </div>
            <div>
              <label className="text-xs">{ht("Conditions")}</label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={conditions}
                onChange={(e) => setConditions(e.target.value)}
                rows={3}
                placeholder={ht("Conditions du contrat...")}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>{ht("Annuler")}
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={!siteId || !label.trim() || mutation.isPending}
            >
              {mutation.isPending ? "En cours..." : initial ? "Modifier" : "Créer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ListeContratsPage() {
  const t = useT();
  const [showCreate, setShowCreate] = useState(false);
  const [edit, setEdit] = useState<Contrat | null>(null);
  const [del, setDel] = useState<Contrat | null>(null);
    const { toast } = useToast();
  const qc = useQueryClient();
  const canEdit = useCan("write");

  const { data: contrats, isLoading } = useQuery({
    queryKey: ["contrats"],
    queryFn: () => contratsApi.list(),
  });
  const { data: sites } = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => contratsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contrats"] });
      toast({ title: ht("Contrat supprimé"), variant: "success" });
      setDel(null);
    },
    onError: (err) => {
      const msg = (err as Error).message;
      const title = msg.startsWith(t("articles.error.delete_impossible"))
        ? t("articles.toast.delete_impossible")
        : t("articles.toast.delete_error");
      toast({
        title,
        description: msg,
        variant: "error",
        duration: 8000,
      });
      setDel(null);
    },
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{ht("Contrats")}</h1>
          <p className="text-muted-foreground">
            Couverture contractuelle par site ({contrats?.length ?? 0} contrats)
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" />
            {t("common.new")} {t("common.contract").toLowerCase()}
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => window.open(`${API_BASE}/v1/exports/contrats/pdf`, "_blank")}
        >
          <FileDown className="mr-1 h-4 w-4" />
          Export PDF
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{ht("Liste des contrats")}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">{ht("Chargement...")}</div>
          ) : !contrats || contrats.length === 0 ? (
            <div className="text-sm text-muted-foreground">{ht("Aucun contrat enregistré.")}</div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2">{ht("ID")}</th>
                  <th className="px-3 py-2">Site</th>
                  <th className="px-3 py-2">{ht("Libellé")}</th>
                  <th className="px-3 py-2">{ht("Conditions")}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {contrats.map((c) => (
                  <tr key={c.id} className="group border-b hover:bg-muted/30">
                    <td className="px-3 py-2 font-mono">#{c.id}</td>
                    <td className="px-3 py-2">#{c.site_id}</td>
                    <td className="px-3 py-2 font-semibold">{c.label}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {c.conditions ? c.conditions.slice(0, 60) + (c.conditions.length > 60 ? "..." : "") : "-"}
                    </td>
                    <td className="px-3 py-2">
                      {canEdit && (
                        <div className="flex gap-1 opacity-70 hover:opacity-100">
                          <button
                            onClick={() => setEdit(c)}
                            className="rounded p-1 text-xs text-muted-foreground hover:bg-muted"
                          >{ht("Éditer")}
                          </button>
                          <button
                            onClick={() => setDel(c)}
                            className="rounded p-1 text-muted-foreground hover:bg-destructive hover:text-destructive-foreground"
                            title={ht("Supprimer")}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </CardContent>
      </Card>

      {showCreate && (
        <ContratModal onClose={() => setShowCreate(false)} sites={sites ?? []} />
      )}
      {edit && (
        <ContratModal
          onClose={() => setEdit(null)}
          sites={sites ?? []}
          initial={edit}
        />
      )}

      <ConfirmDialog
        open={del !== null}
        title={ht("Supprimer ce contrat ?")}
        description={`Cette action supprimera définitivement le contrat "${del?.label ?? ""}". Irréversible.`}
        confirmLabel={ht("Supprimer")}
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => del && deleteMutation.mutate(del.id)}
        onCancel={() => setDel(null)}
      />
    </div>
  );
}
